import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { validatePhoneNumber, formatPhoneNumber, formatPhoneNumberInput } from '../../utils/phoneValidation';
import { BIRTH_MONTHS, optionalZipValue } from '../../utils/profileFields';
import './NewUserPage.css';
import './DependentsPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

interface ParentInfo {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  zip_code: string;
  emergency_first_name: string;
  emergency_last_name: string;
  emergency_phone_number: string;
  emergency_relationship: string;
}

interface Dependent {
  id: number;
  first_name: string;
  last_name: string;
  passcode: string;
  is_minor: boolean;
  liability_waiver_accepted: boolean;
  liability_waiver_expires_at?: string;
}

interface DependentForm {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  zipCode: string;
  birthMonth: string;
  emergencyFirstName: string;
  emergencyLastName: string;
  emergencyRelationship: string;
  emergencyPhoneNumber: string;
  liabilityWaiverAccepted: boolean;
}

const emptyForm = (): DependentForm => ({
  firstName: '',
  lastName: '',
  phoneNumber: '',
  zipCode: '',
  birthMonth: '',
  emergencyFirstName: '',
  emergencyLastName: '',
  emergencyRelationship: 'Parent',
  emergencyPhoneNumber: '',
  liabilityWaiverAccepted: false,
});

const DependentsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const passcode = location.state?.passcode || localStorage.getItem('currentPasscode') || '';

  const [canAdd, setCanAdd] = useState(false);
  const [parent, setParent] = useState<ParentInfo | null>(null);
  const [dependents, setDependents] = useState<Dependent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<DependentForm>(emptyForm());
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [generatedPin, setGeneratedPin] = useState('');

  const prefillFromParent = useCallback((info: ParentInfo) => {
    setFormData({
      ...emptyForm(),
      phoneNumber: formatPhoneNumber(info.phone_number || ''),
      zipCode: optionalZipValue(info.zip_code || ''),
      emergencyFirstName: info.first_name || info.emergency_first_name || '',
      emergencyLastName: info.last_name || info.emergency_last_name || '',
      emergencyRelationship: 'Parent',
      emergencyPhoneNumber: formatPhoneNumber(info.emergency_phone_number || info.phone_number || ''),
    });
  }, []);

  const loadDependents = useCallback(async () => {
    if (!passcode) {
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/dependents/${passcode}`);
      const data = await response.json();
      if (!response.ok || !data.success) {
        setMessage(data.message || 'Unable to load dependents');
        return;
      }
      setCanAdd(Boolean(data.can_add));
      setParent(data.parent || null);
      setDependents(data.dependents || []);
      if (data.parent) {
        prefillFromParent(data.parent);
      }
    } catch {
      setMessage('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }, [passcode, prefillFromParent]);

  useEffect(() => {
    loadDependents();
  }, [loadDependents]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    let processedValue = value;
    if (name === 'phoneNumber' || name === 'emergencyPhoneNumber') {
      processedValue = formatPhoneNumberInput(value);
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : processedValue,
    }));
  };

  const validateForm = () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setMessage('Dependent first and last name are required');
      return false;
    }
    if (!formData.phoneNumber.trim()) {
      setMessage('Phone number is required');
      return false;
    }
    const phoneValidation = validatePhoneNumber(formData.phoneNumber);
    if (!phoneValidation.isValid) {
      setMessage('Phone number: ' + (phoneValidation.error || 'Invalid format'));
      return false;
    }
    if (!formData.emergencyFirstName.trim() || !formData.emergencyLastName.trim()) {
      setMessage('Emergency contact name is required');
      return false;
    }
    if (!formData.emergencyRelationship.trim()) {
      setMessage('Emergency contact relationship is required');
      return false;
    }
    const emergencyPhoneValidation = validatePhoneNumber(formData.emergencyPhoneNumber);
    if (!emergencyPhoneValidation.isValid) {
      setMessage('Emergency phone number: ' + (emergencyPhoneValidation.error || 'Invalid format'));
      return false;
    }
    if (!formData.liabilityWaiverAccepted) {
      setMessage('You must accept the liability waiver on behalf of this dependent');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSaving(true);
    setMessage('');
    setGeneratedPin('');

    try {
      const response = await fetch(`${API_BASE_URL}/dependents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          parent_passcode: passcode,
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone_number: validatePhoneNumber(formData.phoneNumber).formatted,
          zip_code: formData.zipCode,
          birth_month: formData.birthMonth,
          emergency_first_name: formData.emergencyFirstName,
          emergency_last_name: formData.emergencyLastName,
          emergency_phone: validatePhoneNumber(formData.emergencyPhoneNumber).formatted,
          emergency_relationship: formData.emergencyRelationship,
          liability_waiver_accepted: formData.liabilityWaiverAccepted,
        }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setGeneratedPin(data.passcode);
        setMessage('');
        await loadDependents();
      } else {
        setMessage(data.message || 'Failed to add dependent');
      }
    } catch {
      setMessage('Failed to connect to server');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/success', { state: { passcode } });
  };

  const childName = `${formData.firstName.trim() || 'this dependent'} ${formData.lastName.trim()}`.trim();

  return (
    <div className="new-user-container">
      <div className="new-user-card dependents-card">
        <div className="new-user-header">
          <div className="logo">
            <img src="/VolunteerHeart.png" alt="FabMo Volunteer" className="logo-image" />
          </div>
          <h1 className="new-user-title">Dependents</h1>
          <p className="new-user-subtitle">
            People linked to your account. They get their own User Code and can check in separately.
          </p>
        </div>

        {loading ? (
          <p className="dependents-status">Loading...</p>
        ) : !canAdd ? (
          <p className="dependents-status">
            This account is already linked as a dependent, so it cannot add others.
          </p>
        ) : (
          <>
            <div className="dependents-list">
              {dependents.length === 0 ? (
                <p className="dependents-empty">No dependents yet.</p>
              ) : (
                dependents.map((dep) => (
                  <div key={dep.id} className="dependent-card">
                    <div className="dependent-name">
                      {dep.first_name} {dep.last_name}
                    </div>
                    <div className="dependent-meta">
                      <span>User Code: {dep.passcode}</span>
                      <span>
                        Waiver: {dep.liability_waiver_accepted ? 'Accepted' : 'Not accepted'}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {!showForm ? (
              <button
                type="button"
                className="submit-button"
                onClick={() => {
                  setShowForm(true);
                  setGeneratedPin('');
                  setMessage('');
                  if (parent) prefillFromParent(parent);
                }}
              >
                Add a dependent
              </button>
            ) : (
              <form onSubmit={handleSubmit} className="registration-form">
                <div className="form-hints">
                  <p>
                    Email stays <strong>{parent?.email}</strong> so you and this dependent share the same inbox.
                  </p>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">
                      First Name <span className="required-star">*</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                      placeholder="Dependent first name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">
                      Last Name <span className="required-star">*</span>
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                      placeholder="Dependent last name"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={parent?.email || ''}
                    className="form-input"
                    disabled
                    readOnly
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phoneNumber">
                    Phone Number <span className="required-star">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="(555)555-5555"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="zipCode">Zip Code (Optional)</label>
                  <input
                    type="text"
                    id="zipCode"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Leave blank to opt out"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="birthMonth">Birth Month (Optional)</label>
                  <select
                    id="birthMonth"
                    name="birthMonth"
                    value={formData.birthMonth}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="">Opt out</option>
                    {BIRTH_MONTHS.map((month) => (
                      <option key={month.value} value={month.value}>{month.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-divider">
                  <div className="divider-line"></div>
                  <span className="divider-text">Emergency Contact Information</span>
                  <div className="divider-line"></div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="emergencyFirstName">
                      First Name <span className="required-star">*</span>
                    </label>
                    <input
                      type="text"
                      id="emergencyFirstName"
                      name="emergencyFirstName"
                      value={formData.emergencyFirstName}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="emergencyLastName">
                      Last Name <span className="required-star">*</span>
                    </label>
                    <input
                      type="text"
                      id="emergencyLastName"
                      name="emergencyLastName"
                      value={formData.emergencyLastName}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="emergencyRelationship">
                      Relationship <span className="required-star">*</span>
                    </label>
                    <select
                      id="emergencyRelationship"
                      name="emergencyRelationship"
                      value={formData.emergencyRelationship}
                      onChange={handleInputChange}
                      required
                      className="form-select"
                    >
                      <option value="">Select Relationship</option>
                      <option value="Parent">Parent</option>
                      <option value="Guardian">Guardian</option>
                      <option value="Spouse">Spouse</option>
                      <option value="Sibling">Sibling</option>
                      <option value="Child">Child</option>
                      <option value="Friend">Friend</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="emergencyPhoneNumber">
                      Phone Number <span className="required-star">*</span>
                    </label>
                    <input
                      type="tel"
                      id="emergencyPhoneNumber"
                      name="emergencyPhoneNumber"
                      value={formData.emergencyPhoneNumber}
                      onChange={handleInputChange}
                      required
                      placeholder="(555)555-5555"
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-divider">
                  <div className="divider-line"></div>
                  <span className="divider-text">Liability Waiver</span>
                  <div className="divider-line"></div>
                </div>

                <div className="liability-waiver-section">
                  <div className="waiver-content">
                    <h3>FabMo Liability Waiver and Code of Conduct</h3>
                    <p>
                      You are accepting this waiver as the parent or guardian of <strong>{childName}</strong>.
                    </p>
                    <h4>1. Photography and Videography</h4>
                    <p>
                      I understand that photography and videography are frequently conducted at FabMo for promotional and informational purposes. By allowing my dependent to be photographed, I understand that I am giving my permission for their image and/or name to be used, without compensation, for the purpose of promoting FabMo and its mission. If I do not wish for them to be photographed, I will inform the photographer.
                    </p>
                    <h4>2. Liability Waiver</h4>
                    <p>
                      I hereby waive and release all claims for damages that I may have against FabMo, its officers, agents or governing Board for all injuries and illnesses of every type, suffered by myself or any minors in my care that may happen while at FabMo. I understand that there are certain known and foreseeable risks of physical injury and illness associated with participation at FabMo. I understand that there may be other risks of which I may not be presently aware. I understand that my dependent is free at any time to refuse to do anything at FabMo that they believe poses a hazard. By submitting this release, I expressly assume all these risks for myself and/or any minors in my care, and will not make any claims against FabMo, its officers, agents or governing Board.
                    </p>
                    <h4>3. Authorization for Medical Treatment</h4>
                    <p>
                      In the event a medical emergency renders my dependent unable to give directives or consent, I hereby give my permission for them to be taken to the nearest available medical facility and have any necessary emergency medical treatment administered. Furthermore, I specifically release all such medical care providers, FabMo and their representatives from any liability in acting in such circumstances.
                    </p>
                    <h4>4. Code of Conduct</h4>
                    <ul>
                      <li>I understand and will promote a supportive community engaged in furthering FabMo&apos;s mission.</li>
                      <li>I will treat all volunteers and community members with kindness, respect, courtesy and dignity.</li>
                      <li>I will follow the rules and policies set forth by the FabMo Board and the instructions of team leaders and volunteer supervisors.</li>
                      <li>I will observe safe work habits and be aware of the safety of others.</li>
                    </ul>
                    <p>
                      By submitting this form, I am acknowledging that I am the parent or guardian of this dependent, I have read and understood this form, and I am agreeing to these terms on their behalf.
                    </p>
                  </div>
                  <div className="waiver-agreement">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="liabilityWaiverAccepted"
                        checked={formData.liabilityWaiverAccepted}
                        onChange={handleInputChange}
                        required
                        className="waiver-checkbox"
                      />
                      <span className="checkbox-text">
                        I am the parent or guardian of this dependent and I agree to the FabMo Liability Waiver and Code of Conduct on their behalf
                      </span>
                    </label>
                  </div>
                </div>

                <button type="submit" className={`submit-button ${isSaving ? 'loading' : ''}`} disabled={isSaving}>
                  {isSaving ? <div className="loading-spinner"></div> : 'Register dependent'}
                </button>

                {message && !generatedPin && <div className="message error">{message}</div>}

                {generatedPin && (
                  <div className="pin-success">
                    <p>
                      Check your email for a welcome message with this dependent&apos;s User Code.
                      <br />
                      If you don&apos;t see it, check your spam/junk folder.
                    </p>
                    <p>Their unique 5-digit User Code is {generatedPin}.</p>
                    <button
                      type="button"
                      className="login-button"
                      onClick={() => {
                        setShowForm(false);
                        setGeneratedPin('');
                        if (parent) prefillFromParent(parent);
                      }}
                    >
                      Done
                    </button>
                  </div>
                )}
              </form>
            )}
          </>
        )}

        <div className="new-user-footer">
          <button onClick={handleBack} className="back-button">
            ← Back to Menu
          </button>
        </div>
      </div>
    </div>
  );
};

export default DependentsPage;
